import toast from "react-hot-toast";
import { SUCCESS_MESSAGES } from "../config/constants";

// Deduplication tracking for preventing duplicate toasts
let lastErrorToast = { message: null, timestamp: 0 };
let lastSuccessToast = { message: null, timestamp: 0 };
let lastWarningToast = { message: null, timestamp: 0 };

// Toast Success
export const showSuccess = (message) => {
  // Prevent duplicate success toasts within 1 second
  const now = Date.now();
  if (
    lastSuccessToast.message === message &&
    now - lastSuccessToast.timestamp < 1000
  ) {
    console.warn("Duplicate success toast prevented:", message);
    return;
  }
  lastSuccessToast = { message, timestamp: now };

  return toast.success(message, {
    duration: 3000,
    position: "bottom-right",
    style: {
      background: "#fff",
      color: "#0a9400",
      padding: "16px",
      borderRadius: "8px",
      fontWeight: "500",
      boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
    },
    iconTheme: {
      primary: "#0a9400",
      secondary: "#fff",
    },
  });
};

// Toast Error
export const showError = (message) => {
  // Prevent duplicate error toasts within 1 second
  const now = Date.now();
  if (
    lastErrorToast.message === message &&
    now - lastErrorToast.timestamp < 1000
  ) {
    console.warn("Duplicate error toast prevented:", message);
    return;
  }
  lastErrorToast = { message, timestamp: now };

  return toast.error(message, {
    duration: 4000,
    position: "bottom-right",
    style: {
      background: "#fff",
      color: "#EF4444",
      padding: "16px",
      borderRadius: "8px",
      fontWeight: "500",
      boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
    },
    iconTheme: {
      primary: "#EF4444",
      secondary: "#fff",
    },
  });
};

// Toast Loading
export const showLoading = (message) => {
  return toast.loading(message, {
    position: "bottom-right",
    style: {
      background: "#fff",
      color: "#3B82F6",
      padding: "16px",
      borderRadius: "8px",
      fontWeight: "500",
      boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
    },
  });
};

// Toast Info
export const showInfo = (message) => {
  return toast(message, {
    duration: 3000,
    position: "bottom-right",
    icon: "ℹ️",
    style: {
      background: "#fff",
      color: "#3B82F6",
      padding: "16px",
      borderRadius: "8px",
      fontWeight: "500",
      boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
    },
  });
};

// Toast Warning
export const showWarning = (message) => {
  // Prevent duplicate warning toasts within 1 second
  const now = Date.now();
  if (
    lastWarningToast.message === message &&
    now - lastWarningToast.timestamp < 1000
  ) {
    console.warn("Duplicate warning toast prevented:", message);
    return;
  }
  lastWarningToast = { message, timestamp: now };

  return toast(message, {
    duration: 3500,
    position: "bottom-right",
    icon: "⚠️",
    style: {
      background: "#fff",
      color: "#F59E0B",
      padding: "16px",
      borderRadius: "8px",
      fontWeight: "500",
      boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
    },
  });
};

// Toast Promise - untuk async operations (lower level)
export const showPromise = (promise, messages) => {
  return toast.promise(
    promise,
    {
      loading: messages.loading || "Loading...",
      success: messages.success || "Success!",
      error: messages.error || "Error!",
    },
    {
      position: "bottom-right",
      style: {
        padding: "16px",
        borderRadius: "8px",
        fontWeight: "500",
      },
    }
  );
};

/**
 * Async Toast - Wrapper untuk operasi async dengan toast otomatis
 * Menampilkan loading toast saat task berjalan, lalu success/error setelah selesai
 *
 * @param {Promise} promise - Promise yang akan dieksekusi
 * @param {Object} messages - Pesan untuk setiap state
 * @param {string|Function} messages.loading - Pesan saat loading
 * @param {string|Function} messages.success - Pesan saat success (bisa function yang menerima result)
 * @param {string|Function} messages.error - Pesan saat error (bisa function yang menerima error)
 * @returns {Promise} - Result dari promise yang dieksekusi
 *
 * @example
 * // Basic usage
 * const result = await asyncToast(
 *   axios.post('/api/data', data),
 *   {
 *     loading: 'Menyimpan data...',
 *     success: 'Data berhasil disimpan!',
 *     error: 'Gagal menyimpan data'
 *   }
 * );
 *
 * @example
 * // With dynamic messages
 * const users = await asyncToast(
 *   fetchUsers(),
 *   {
 *     loading: 'Memuat users...',
 *     success: (data) => `Berhasil memuat ${data.length} users`,
 *     error: (err) => `Error: ${err.message}`
 *   }
 * );
 */
export const asyncToast = async (promise, messages) => {
  return toast.promise(
    promise,
    {
      loading: messages.loading || "Loading...",
      success: messages.success || "Success!",
      error: messages.error || "Error!",
    },
    {
      position: "bottom-right",
      style: {
        padding: "16px",
        borderRadius: "8px",
        fontWeight: "500",
        boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
      },
      success: {
        duration: 3000,
        style: {
          background: "#fff",
          color: "#0a9400",
        },
        iconTheme: {
          primary: "#0a9400",
          secondary: "#fff",
        },
      },
      error: {
        duration: 4000,
        style: {
          background: "#fff",
          color: "#EF4444",
        },
        iconTheme: {
          primary: "#EF4444",
          secondary: "#fff",
        },
      },
      loading: {
        style: {
          background: "#fff",
          color: "#3B82F6",
        },
      },
    }
  );
};

/**
 * Helper functions untuk operasi CRUD umum
 */

// Create/Add operation
export const toastCreate = (promise, itemName = "data") => {
  return asyncToast(promise, {
    loading: `Menambahkan ${itemName}...`,
    success: SUCCESS_MESSAGES.CREATE.replace("Data", itemName),
    error: `Gagal menambahkan ${itemName}`,
  });
};

// Update/Edit operation
export const toastUpdate = (promise, itemName = "data") => {
  return asyncToast(promise, {
    loading: `Menyimpan perubahan ${itemName}...`,
    success: SUCCESS_MESSAGES.UPDATE.replace("Data", itemName),
    error: `Gagal memperbarui ${itemName}`,
  });
};

// Delete operation
export const toastDelete = (promise, itemName = "data") => {
  return asyncToast(promise, {
    loading: `Menghapus ${itemName}...`,
    success: SUCCESS_MESSAGES.DELETE.replace("Data", itemName),
    error: `Gagal menghapus ${itemName}`,
  });
};

// Fetch/Load operation
export const toastFetch = (promise, itemName = "data") => {
  return asyncToast(promise, {
    loading: `Memuat ${itemName}...`,
    success: `${itemName} berhasil dimuat!`,
    error: `Gagal memuat ${itemName}`,
  });
};
